/*eslint complexity: ["error", 32]*/
import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import * as Icon from 'react-bootstrap-icons';

import { Grid } from '../../../common/components/Grid/Grid';
import {
  GridColumn,
  GridCountFunction,
  GridGetterFunction,
  GridRecord,
} from '../../../common/components/Grid/GridDataTypes';
import momencik from '../../../common/momencik';
import {
  GetSalesInvoiceDto,
  GetSalesInvoiceDtoStatusEnum,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import mathUtils from '../../../utils/mathUtils';
import numberFormatter from '../../../common/numberFormatter';
import { SalesInvoiceStatusEnumText } from '../../../common/spdCore/validation/enums';

import { SalesInvoiceCorrectionButton } from './SalesInvoiceCorrectionButton';
import { SalesInvoiceOpenInNewTabButton } from './SalesInvoiceOpenInNewTabButton';

const PaymentDot: React.FC<{ invoice: GetSalesInvoiceDto }> = ({ invoice }) => {
  const remaining = mathUtils.parse(invoice.remainingLinkableAmount ?? '0');
  const linkable = mathUtils.parse(invoice.amountGross ?? '0');

  let statusClass: string;
  let label: string;

  if (remaining < 0) {
    statusClass = 'overpaid';
    label = 'Nadpłata';
  } else if (invoice.isPaid || remaining === 0) {
    statusClass = 'paid';
    label = 'W pełni opłacona';
  } else if (remaining >= linkable) {
    statusClass = 'unpaid';
    label = 'Nieopłacona';
  } else {
    statusClass = 'partial';
    label = 'Częściowo opłacona';
  }

  return (
    <OverlayTrigger placement="bottom" overlay={<Tooltip>{label}</Tooltip>}>
      <span className={`payment-status-dot ${statusClass}`} />
    </OverlayTrigger>
  );
};

interface SalesInvoiceSearchProps {
  getData: GridGetterFunction;
  getCount: GridCountFunction;
  onRowClick?: (key: string) => Promise<void> | void | undefined;
  refresh?: boolean;
  withFilterInColumns?: boolean;
  showFilter?: boolean;
  withoutCorrectionButton?: boolean;
  enableMultiSelect?: boolean;
  selectedKeys?: string[];
  showExport?: boolean;
  withHistory?: boolean;
}

export const salesInvoiceForGrid = (
  invoice: GetSalesInvoiceDto,
): GridRecord => {
  return {
    key: invoice.id,
    values: {
      id: invoice.id,
      'salesInvoice.systemNumber': (
        <>
          {invoice.systemNumber}{' '}
          {invoice.originalInvoice?.id && (
            <OverlayTrigger
              placement="bottom"
              overlay={
                <Tooltip>
                  Korekta do faktury {invoice.originalInvoice?.number}
                </Tooltip>
              }
            >
              <Icon.FileEarmarkMinusFill className="icon-blue me-2" />
            </OverlayTrigger>
          )}
        </>
      ),
      'salesInvoice.number': invoice.number || '-',
      'salesInvoice.invoiceDate': momencik(invoice.invoiceDate, 'YYYY-MM-DD'),
      'salesInvoice.saleDate': momencik(invoice.saleDate, 'YYYY-MM-DD'),
      'salesInvoice.dueDate': momencik(invoice.dueDate, 'YYYY-MM-DD'),
      'company.name': invoice.company?.name || '',
      'examined.surname': invoice.examined?.id
        ? invoice.examined?.surname + ' ' + invoice.examined?.firstname
        : '',
      sales_invoice_invoice_buyer_nip:
        invoice.status === GetSalesInvoiceDtoStatusEnum.Draft
          ? invoice.company?.nip || invoice.examined?.nip || ''
          : invoice.invoiceBuyerNip || '',
      sales_invoice_invoice_buyer_pesel:
        invoice.status === GetSalesInvoiceDtoStatusEnum.Draft
          ? !invoice.examined?.nip
            ? invoice.examined?.pesel || ''
            : ''
          : invoice.invoiceBuyerPesel || '',
      sales_invoice_invoice_buyer_tax_identifier:
        invoice.status === GetSalesInvoiceDtoStatusEnum.Draft
          ? invoice.company?.taxIdentifier || ''
          : invoice.invoiceBuyerTaxIdentifier || '',
      'salesInvoice.amountNet': numberFormatter(invoice.amountNet, true),
      'salesInvoice.amountGross': numberFormatter(invoice.amountGross, true),
      'salesInvoice.remainingLinkableAmount':
        numberFormatter(invoice.remainingLinkableAmount, true) || '—',
      'payment-status': <PaymentDot invoice={invoice} />,
      'salesInvoice.status': SalesInvoiceStatusEnumText[invoice.status],
      'correction-button': <SalesInvoiceCorrectionButton invoice={invoice} />,
      'open-new-tab': (
        <SalesInvoiceOpenInNewTabButton salesInvoiceId={invoice.id} />
      ),
      'creator.name': invoice?.creator?.name || '',
      //   'salesInvoice.statusKSeF': invoice.statusKSeF,
    },
    isInactive: !invoice.active,
    tdCssClass: {
      'salesInvoice.amountNet': 'amounts-align-right',
      'salesInvoice.amountGross': 'amounts-align-right',
      'salesInvoice.remainingLinkableAmount': 'amounts-align-right',
    },
  };
};

export const SalesInvoiceSearch: React.FC<SalesInvoiceSearchProps> = props => {
  const columns: GridColumn[] = [
    {
      header: '',
      property: 'open-new-tab',
      noFilter: true,
      noSorting: true,
    },
    { header: 'Nr sys.', property: 'salesInvoice.systemNumber' },
    { header: 'Nr faktury', property: 'salesInvoice.number' },
    {
      header: 'Data wystawienia',
      property: 'salesInvoice.invoiceDate',
    },
    { header: 'Data sprzedaży', property: 'salesInvoice.saleDate' },
    {
      header: 'Termin płatności',
      property: 'salesInvoice.dueDate',
    },
    { header: 'Firma', property: 'company.name' },
    { header: 'Badany', property: 'examined.surname' },
    { header: 'NIP z faktury', property: 'sales_invoice_invoice_buyer_nip' },
    {
      header: 'Pesel z faktury',
      property: 'sales_invoice_invoice_buyer_pesel',
    },
    {
      header: 'Ident. pod. z faktury',
      property: 'sales_invoice_invoice_buyer_tax_identifier',
    },
    {
      header: 'Kwota netto',
      property: 'salesInvoice.amountNet',
    },
    {
      header: 'Kwota brutto',
      property: 'salesInvoice.amountGross',
    },
    {
      header: 'Pozostało do dowiązania',
      property: 'salesInvoice.remainingLinkableAmount',
      noFilter: true,
      noSorting: true,
    },
    {
      header: '',
      property: 'payment-status',
      noFilter: true,
      noSorting: true,
    },
    {
      header: 'Utworzona przez',
      property: 'creator.name',
    },
    {
      header: 'Status',
      property: 'salesInvoice.status',
      noFilter: true,
    },
    ...(props.withoutCorrectionButton
      ? []
      : [
          {
            header: '',
            property: 'correction-button',
            noFilter: true,
            noSorting: true,
          },
        ]),
    // {
    //   header: 'Status KSeF',
    //   property: 'salesInvoice.statusKSeF',
    //   noFilter: true,
    // },
  ];

  return (
    <Grid
      getData={props.getData}
      getDataCount={props.getCount}
      columns={columns}
      showFilter={props.showFilter}
      initialOrderColumn="salesInvoice.systemNumber"
      initialOrderDirection="DESC"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={'Brak faktur sprzedaży w systemie'}
      onRowClick={props.onRowClick}
      refresh={props.refresh}
      withHistory={props.withHistory ?? true}
      withFilterInColumns={props.withFilterInColumns}
      enableMultiSelect={props.enableMultiSelect}
      selectedKeys={props.selectedKeys}
      showExport={props.showExport}
    />
  );
};
